import SelectedFilters from "./selectedfilters.js";
import DataService from "./dataservice.js";
import FilterValues from "./filtervalues.js";
import { prodListValues } from './shared.js';

const advancedSearchUrl = document.getElementById('QueryUrl');

class UpdateFilterUrl {
    /**
     * Updates the filter URL based on the given filter values and product list values.
     * @param {FilterValues} filterValues 
     * @param {typeof prodListValues} prodListValues 
     * @param {boolean} updatePageNumber 
     * @param {boolean} updateUrl 
     * @param {boolean} forceGetProducts 
     * @returns {void}
     */
    update(filterValues, prodListValues, updatePageNumber, updateUrl, forceGetProducts) {
        if (typeof (updateUrl) === 'undefined') updateUrl = true;
        if (typeof (forceGetProducts) === 'undefined') forceGetProducts = false;

        const pathName = this.urlPath();

        let newPath = this.updatedPriceFilterRouteParam(filterValues.getPriceFilter()) +
            this.updatedColorFilterRouteParam(filterValues.getColorFilter()) +
            this.updatedRatingFilterRouteParam(filterValues.getRatingFilter()) +
            this.updatedProductQuantityFilterRouteParam(filterValues.getProductQuantityFilter()) +
            this.updatedProdTimeRouteParam(filterValues.getProdTimeFilter()) +
            this.updatedImprintLocationCountRouteParam(filterValues.getImprintLocationCountFilter()) +
            this.updatedImprintColorCountRouteParam(filterValues.getImprintColorCountFilter()) +
            this.updatedTagFilterRouteParam(filterValues.getTagFilter());

        if (filterValues.getColorFilter().length === 0 && filterValues.getRatingFilter().length === 0 && filterValues.getProdTimeFilter().length === 0 && filterValues.getTagFilter().length === 0 &&
            filterValues.getPriceFilter().length === 0 && filterValues.getSubSearchFilter().length === 0 && filterValues.getProductQuantityFilter().length === 0 &&
            filterValues.getImprintLocationCountFilter().length === 0 && filterValues.getImprintColorCountFilter().length === 0) {
            SelectedFilters.removeAllFromSelectedFilter();
        }

        // If filtering is applied then set the page to the beginning in case the number of pages decreases.
        if (updatePageNumber !== true) {
            filterValues.addPageNumber(1);
        }

        newPath += this.updatedFilterQueryString(filterValues.getPageNumber(), prodListValues.sortMap, filterValues.getSortFilter(), filterValues.getSubSearchFilter(), filterValues.getPageSize());

        if (!forceGetProducts && window.location.pathname + window.location.search === pathName + newPath) {
            return;
        }

        this.updateCanonicalUrl(newPath);

        newPath = pathName + newPath;

        DataService.getProducts(newPath, updateUrl, newPath);
    };

    /**
     * Updates the canonical URL with the given path.
     * @param {string} path 
     * @returns {void}
     */
    updateCanonicalUrl(path) {
        const canonical = document.querySelector('link[rel="canonical"]');
        if (canonical !== null) {
            canonical.href = this.getBaseUrl(canonical.href) + path;
        }

        const amp = document.querySelector('link[rel="amphtml"]');
        if (amp !== null) {
            amp.href = this.getBaseUrl(amp.href) + path;
        }
    };

    /**
     * Gets the base URL from the given URL by removing query parameters and route parameters.
     * @param {string} url 
     * @returns {string}
     */
    getBaseUrl(url) {
        const matches = prodListValues.paramPattern.exec(url);
        let baseUrl = '';

        if (matches !== null) {
            baseUrl = url.slice(0, matches.index);
        } else if (url.indexOf('?') >= 0) {
            baseUrl = url.slice(0, url.indexOf('?'));
        } else {
            baseUrl = url;
        }

        return baseUrl;
    };

    /**
     * Updates the price filter route parameter based on the given price range.
     * @param {number[]} priceRange 
     * @returns {string}
     */
    updatedPriceFilterRouteParam(priceRange) {
        let priceFilter = '';

        if (priceRange.length > 1) {
            let priceMin = priceRange[0];
            let priceMax = priceRange[1];
            priceFilter = '/p/' + (priceMin * 100).toFixed() + '-' + (priceMax * 100).toFixed();
        }

        return priceFilter;
    };

    /**
     * Updates the color filter route parameter based on the given colors.
     * @param {string[]} colors 
     * @returns {string}
     */
    updatedColorFilterRouteParam(colors) {
        let colorFilters = '';
        if (colors.length) {
            colorFilters = '/c/' + colors.join('-');
        }
        return colorFilters;
    };

    /**
     * Updates the rating filter route parameter based on the given ratings.
     * @param {string[]} ratings 
     * @returns {string}
     */
    updatedRatingFilterRouteParam(ratings) {
        let ratingFilters = '';
        if (ratings.length) {
            ratingFilters = '/r/' + ratings.join('-');
        }
        return ratingFilters;
    };

    /**
     * Updates the product quantity filter route parameter based on the given product quantity.
     * @param {string[]} productQuantity 
     * @returns {string}
     */
    updatedProductQuantityFilterRouteParam(productQuantity) {
        let productQuantityFilter = '';
        if (productQuantity.length) {
            productQuantityFilter = '/mq/' + productQuantity;
        }
        return productQuantityFilter;
    };

    /**
     * Updates the product time filter route parameter based on the given product times.
     * @param {string[]} prodTimes 
     * @returns {string}
     */
    updatedProdTimeRouteParam(prodTimes) {
        let timeFilter = '';
        if (prodTimes.length) {
            timeFilter = '/s/' + prodTimes.join('-');
        }
        return timeFilter;
    };

    /**
     * Updates the imprint location count filter route parameter based on the given imprint location counts.
     * @param {string[]} imprintLocationCounts 
     * @returns {string}
     */
    updatedImprintLocationCountRouteParam(imprintLocationCounts) {
        let imprintLocationCountFilter = '';
        if (imprintLocationCounts.length) {
            imprintLocationCountFilter = '/l/' + imprintLocationCounts.join('-');
        }
        return imprintLocationCountFilter;
    };

    /**
     * Updates the imprint color count filter route parameter based on the given imprint color counts.
     * @param {string[]} imprintColorCounts 
     * @returns {string}
     */
    updatedImprintColorCountRouteParam(imprintColorCounts) {
        let imprintColorCountFilter = '';
        if (imprintColorCounts.length) {
            imprintColorCountFilter = '/m/' + imprintColorCounts.join('-');
        }
        return imprintColorCountFilter;
    };

    /**
     * Updates the tag filter route parameter based on the given tags.
     * @param {string[]} tags 
     * @returns {string}
     */
    updatedTagFilterRouteParam(tags) {
        let tagFilter = '';

        if (tags.length) {
            tagFilter = '/t/' + tags.join('-');
        }
        return tagFilter;
    };

    /**
     * Updates the filter query string based on the given parameters.
     * @param {number} pageNo 
     * @param {Array} sortMap 
     * @param {string} sort 
     * @param {string} subSearch 
     * @param {number} pageSize 
     * @returns {string} The updated query string.

     */
    updatedFilterQueryString(pageNo, sortMap, sort, subSearch, pageSize) {
        let newQueryString = '?';

        if (advancedSearchUrl && advancedSearchUrl.value) {
            newQueryString += advancedSearchUrl.value + '&';
        }

        if (pageNo && parseInt(pageNo) !== 1) {
            newQueryString += 'page=' + pageNo + '&';
        }

        if (pageSize && parseInt(pageSize) !== FilterValues.getDefaultPageSize()) {
            newQueryString += 'PS=' + pageSize + '&';
        }

        if (sort && sort !== FilterValues.getDefaultSort()) {
            for (let i = 0; i < sortMap.length; i++) {
                if (sortMap[i].v.search(new RegExp(sort, 'i')) > -1) {
                    newQueryString += sortMap[i].q + '&';
                    break;
                }
            }
        }

        if (subSearch) {
            newQueryString += 'ss=' + subSearch + '&';
        }

        return newQueryString.substr(0, newQueryString.length - 1);
    };

    /**
     * Updates the page size in the URL query string.
     * @param {number} pageSize 
     * @return {void}
     */
    updatePageSize(pageSize) {
        const currentUrl = window.location.href;
        const pageSizeQueryString = 'PS=' + pageSize;
        const newUrl = currentUrl + (currentUrl.indexOf('?') > -1 ? '&' : '?') + pageSizeQueryString;

        history.replaceState({ title: document.getElementById('BaseTitle').value, keywords: document.getElementById('BaseKeywords').value, description: document.getElementById('BaseDescription').value }, '', newUrl);
    };

    /**
     * Gets the URL path without the protocol and host.
     * @returns {string} The URL path.
     */
    urlPath() {
        const currentUrl = window.location.href;
        let baseUrl = '';

        if (document.getElementById('FilterFriendlyUrl').value.length > 0) {
            baseUrl = document.getElementById('FilterFriendlyUrl').value;
        } else {
            baseUrl = this.getBaseUrl(currentUrl);
        }
        const urlArray = currentUrl.split('/');
        const prot = urlArray[0];
        const host = urlArray[2];
        const url = prot + '//' + host;

        return baseUrl.replace(url, '');
    };
}

export default new UpdateFilterUrl();